import type { BracketTree, BracketTreeMatch, BracketTreeRound } from './playEventDrawPreview';

export interface BracketKioskSegment {
  id: string;
  label: string;
  roundNumbers: number[];
  /** When set, only the top or bottom branch matches are shown within each round. */
  bracketHalf?: 'top' | 'bottom';
}

const DEFAULT_ROUND_WINDOW = 2;
const SPLIT_BRACKET_R1_THRESHOLD = 8;
const SINGLE_SEGMENT_R1_THRESHOLD = 4;

function labelRoundWindow(tree: BracketTree, roundNumbers: number[]): string {
  if (roundNumbers.length === 0) return 'Bracket';
  if (roundNumbers.length === 1) {
    const round = tree.rounds.find((item) => item.roundNumber === roundNumbers[0]);
    return round?.label ?? `Round ${roundNumbers[0]}`;
  }
  const first = tree.rounds.find((item) => item.roundNumber === roundNumbers[0]);
  const last = tree.rounds.find((item) => item.roundNumber === roundNumbers[roundNumbers.length - 1]);
  return `${first?.label ?? roundNumbers[0]} – ${last?.label ?? roundNumbers[roundNumbers.length - 1]}`;
}

function chunkRoundNumbers(roundNumbers: number[], windowSize: number): number[][] {
  const windows: number[][] = [];
  for (let index = 0; index < roundNumbers.length; index += windowSize) {
    windows.push(roundNumbers.slice(index, index + windowSize));
  }
  return windows;
}

function appendRoundWindows(
  segments: BracketKioskSegment[],
  tree: BracketTree,
  roundNumbers: number[],
  prefix: string,
  bracketHalf?: 'top' | 'bottom'
): void {
  const halfSuffix =
    bracketHalf === 'top' ? ' · Top' : bracketHalf === 'bottom' ? ' · Bottom' : '';

  for (const window of chunkRoundNumbers(roundNumbers, DEFAULT_ROUND_WINDOW)) {
    segments.push({
      id: `${prefix.toLowerCase().replace(/\s+/g, '-')}-r${window.join('-')}${bracketHalf ? `-${bracketHalf}` : ''}`,
      label: `${prefix}${halfSuffix} · ${labelRoundWindow(tree, window)}`,
      roundNumbers: window,
      bracketHalf,
    });
  }
}

/**
 * Splits a single-elimination bracket into kiosk/TV segments for horizontal carousel
 * navigation. Large draws use top/bottom halves; medium draws use round windows only.
 */
export function getBracketKioskSegments(tree: BracketTree): BracketKioskSegment[] {
  if (tree.roundCount === 0 || tree.rounds.length === 0) {
    return [];
  }

  const roundNumbers = tree.rounds.map((round) => round.roundNumber);
  const roundOne = tree.rounds.find((round) => round.roundNumber === 1);
  const roundOneMatchCount = roundOne?.matches.length ?? 0;

  if (tree.roundCount <= 2 || roundOneMatchCount <= SINGLE_SEGMENT_R1_THRESHOLD) {
    return [
      {
        id: 'bracket-full',
        label: 'Bracket',
        roundNumbers,
      },
    ];
  }

  const segments: BracketKioskSegment[] = [];

  if (roundOneMatchCount > SPLIT_BRACKET_R1_THRESHOLD) {
    const earlyRounds = roundNumbers.filter((roundNumber) => roundNumber < tree.roundCount - 1);
    const championshipRounds = roundNumbers.filter((roundNumber) => roundNumber >= tree.roundCount - 1);

    appendRoundWindows(segments, tree, earlyRounds, 'Top half', 'top');
    appendRoundWindows(segments, tree, earlyRounds, 'Bottom half', 'bottom');

    if (championshipRounds.length > 0) {
      segments.push({
        id: 'championship',
        label: labelRoundWindow(tree, championshipRounds),
        roundNumbers: championshipRounds,
      });
    }

    return segments;
  }

  appendRoundWindows(segments, tree, roundNumbers, 'Bracket');
  return segments;
}

export function filterRoundMatchesForSegment(
  round: BracketTreeRound,
  segment: BracketKioskSegment
): BracketTreeMatch[] {
  if (!segment.bracketHalf) {
    return round.matches;
  }

  const midpoint = Math.ceil(round.matches.length / 2);
  if (segment.bracketHalf === 'top') {
    return round.matches.slice(0, midpoint);
  }
  return round.matches.slice(midpoint);
}

export function getSegmentRounds(tree: BracketTree, segment: BracketKioskSegment): BracketTreeRound[] {
  return tree.rounds
    .filter((round) => segment.roundNumbers.includes(round.roundNumber))
    .map((round) => ({
      ...round,
      matches: filterRoundMatchesForSegment(round, segment),
    }))
    .filter((round) => round.matches.length > 0);
}
